import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import ChatAssistant from '../../components/feature/ChatAssistant';
import InsuranceStatsSection from './components/InsuranceStatsSection';
import CountdownTimer from '../../components/feature/CountdownTimer';
import { useEffect } from 'react';
import SEOHead from '../../components/feature/SEOHead';

export default function SegurosMalContratadosPage() {
  const commonMistakes = [
    {
      number: '01',
      title: 'Escolher seguro apenas pelo preço',
      description: 'Focar apenas no preço pode resultar em coberturas inadequadas.',
      icon: 'ri-money-dollar-circle-line',
      color: 'from-red-500 to-red-700'
    },
    {
      number: '02',
      title: 'Não entender franquias e limites',
      description: 'Muitos segurados não analisam corretamente franquias, limites e exclusões.',
      icon: 'ri-file-list-line',
      color: 'from-orange-500 to-orange-700'
    },
    {
      number: '03',
      title: 'Informar dados incorretos',
      description: 'Informações incorretas podem gerar problemas na hora do sinistro.',
      icon: 'ri-error-warning-line',
      color: 'from-amber-500 to-amber-700'
    },
    {
      number: '04',
      title: 'Escolher coberturas inadequadas',
      description: 'Coberturas mal escolhidas podem deixar o patrimônio exposto.',
      icon: 'ri-shield-cross-line',
      color: 'from-yellow-500 to-yellow-700'
    },
    {
      number: '05',
      title: 'Definir valor segurado errado',
      description: 'Valores inadequados podem reduzir drasticamente a indenização.',
      icon: 'ri-calculator-line',
      color: 'from-lime-500 to-lime-700'
    },
    {
      number: '06',
      title: 'Renovar o seguro sem revisão técnica',
      description: 'Muitos segurados renovam automaticamente sem revisar as coberturas.',
      icon: 'ri-refresh-line',
      color: 'from-green-500 to-green-700'
    },
    {
      number: '07',
      title: 'Negociar sozinho com seguradoras',
      description: 'Sem conhecimento técnico o segurado pode pagar mais caro ou contratar proteção inadequada.',
      icon: 'ri-user-line',
      color: 'from-teal-500 to-teal-700'
    }
  ];

  const ibxServices = [
    {
      title: 'Análise técnica da apólice',
      description: 'Revisão completa de todas as cláusulas, coberturas e exclusões do contrato.',
      icon: 'ri-file-search-line'
    },
    {
      title: 'Avaliação de riscos',
      description: 'Identificação dos riscos reais do seu negócio ou patrimônio.',
      icon: 'ri-shield-check-line'
    },
    {
      title: 'Definição adequada de coberturas',
      description: 'Escolha estratégica das coberturas necessárias para sua proteção.',
      icon: 'ri-checkbox-multiple-line'
    },
    {
      title: 'Negociação estratégica',
      description: 'Negociação profissional com seguradoras para obter as melhores condições.',
      icon: 'ri-hand-coin-line'
    }
  ];

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Seguros Mal Contratados - Riscos e Soluções",
    "description": "Entenda os riscos de seguros mal contratados e como evitar prejuízos com análise técnica especializada.",
    "url": "https://ibxbrasil.com.br/seguros-mal-contratados"
  };

  return (
    <>
      <SEOHead
        title="Seguros Mal Contratados - Riscos e Como Evitar | IBX Brasil"
        description="Descubra os riscos de seguros empresariais mal contratados: coberturas insuficientes, cláusulas prejudiciais e como proteger sua empresa adequadamente."
        canonical="https://ibxbrasil.com.br/seguros-mal-contratados"
        ogImage="https://readdy.ai/api/search-image?query=business%20owner%20worried%20about%20inadequate%20insurance%20coverage%20with%20insurance%20policy%20documents%20showing%20gaps%20and%20risks&width=1200&height=630&seq=ibx-mal-contratados-og&orientation=landscape"
        schema={webPageSchema}
      />
      <div className="min-h-screen bg-white">
        <Navbar />
        
        {/* Hero Section */}
        <section className="relative min-h-[600px] flex items-center justify-center overflow-hidden pt-20">
          {/* Background Image */}
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url('https://readdy.ai/api/search-image?query=professional%20business%20insurance%20consultant%20analyzing%20documents%20with%20client%20in%20modern%20office%20environment%2C%20serious%20atmosphere%20showing%20risk%20management%20and%20contract%20review%2C%20clean%20corporate%20setting%20with%20natural%20lighting%20and%20professional%20workspace&width=1920&height=1080&seq=ibx-bad-insurance-hero&orientation=landscape')`
            }}
          ></div>
          
          {/* Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-petrol-950/85 via-petrol-900/80 to-petrol-950/85"></div>
          
          {/* Content */}
          <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-20">
            <div className="inline-block mb-6">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-action-orange-500/20 backdrop-blur-sm border border-action-orange-500/30 rounded-full text-action-orange-300 text-sm font-semibold">
                <i className="ri-shield-check-line text-base"></i>
                GESTÃO DE RISCOS E CONTRATAÇÃO PROFISSIONAL DE SEGUROS
              </span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Seguros mal contratados podem gerar prejuízos inesperados
            </h1>
            
            <p className="text-lg sm:text-xl text-gray-200 leading-relaxed max-w-3xl mx-auto mb-8">
              Muitos segurados acreditam que estão protegidos até o momento em que precisam utilizar o seguro.
              Quando isso acontece, descobrem que a cobertura não era adequada ou que a contratação foi feita sem análise técnica.
            </p>
            
            <p className="text-base sm:text-lg text-gray-300 leading-relaxed max-w-3xl mx-auto">
              A IBX Brasil atua justamente para evitar esse tipo de situação, profissionalizando a contratação de seguros.
            </p>
          </div>
        </section>

        {/* Stats Section */}
        <InsuranceStatsSection />

        {/* Common Mistakes Section */}
        <section className="py-16 sm:py-20 lg:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            
            <div className="text-center max-w-4xl mx-auto mb-12 sm:mb-16">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-petrol-900 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Os 7 erros mais comuns na contratação de seguros
              </h2>
              <p className="text-lg text-gray-600">
                Conheça os principais erros que podem comprometer sua proteção e gerar prejuízos financeiros.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {commonMistakes.map((mistake, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 p-6 sm:p-8 border border-gray-100 hover:border-petrol-200 group"
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className={`w-14 h-14 bg-gradient-to-br ${mistake.color} rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform`}>
                      <i className={`${mistake.icon} text-2xl text-white`}></i>
                    </div>
                    <div className="text-4xl font-bold text-gray-200 group-hover:text-petrol-200 transition-colors" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {mistake.number}
                    </div>
                  </div>
                  
                  <h3 className="text-xl font-bold text-petrol-900 mb-3">
                    {mistake.title}
                  </h3>
                  
                  <p className="text-base text-gray-600 leading-relaxed">
                    {mistake.description}
                  </p>
                </div>
              ))}
            </div>

          </div>
        </section>

        {/* How to Avoid Section */}
        <section className="py-16 sm:py-20 lg:py-24 bg-gradient-to-br from-petrol-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            
            <div className="text-center max-w-4xl mx-auto mb-12 sm:mb-16">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-petrol-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                Como evitar esses prejuízos
              </h2>
              <p className="text-lg sm:text-xl text-gray-700 leading-relaxed">
                A contratação de seguros pode ser profissionalizada. A IBX Brasil oferece gestão completa do processo, garantindo que você tenha a proteção adequada pelo melhor custo-benefício.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-12">
              {ibxServices.map((service, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 border border-gray-100 hover:border-petrol-300 transition-all duration-300 hover:shadow-xl"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-petrol-500 to-petrol-700 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                      <i className={`${service.icon} text-2xl text-white`}></i>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-petrol-900 mb-3">
                        {service.title}
                      </h3>
                      <p className="text-base text-gray-600 leading-relaxed">
                        {service.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Countdown Timer */}
            <div className="mb-8">
              <CountdownTimer location="seguros-mal-contratados" />
            </div>

            {/* CTA Box */}
            <div className="bg-gradient-to-br from-petrol-700 to-petrol-900 rounded-2xl shadow-2xl p-8 sm:p-12 text-center">
              <div className="max-w-3xl mx-auto">
                <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Profissionalize a contratação dos seus seguros
                </h3>
                <p className="text-base sm:text-lg text-gray-200 leading-relaxed mb-8">
                  Evite prejuízos e garanta que seu patrimônio esteja realmente protegido. Fale com nossos especialistas e descubra como a IBX Brasil pode ajudar.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <a
                    href="https://wa.me/5577988080505?text=Ol%C3%A1%2C%20gostaria%20de%20profissionalizar%20a%20contrata%C3%A7%C3%A3o%20dos%20meus%20seguros."
                    target="_blank"
                    rel="nofollow noreferrer"
                    className="inline-flex items-center gap-3 bg-action-orange-500 text-white px-8 py-4 rounded-xl text-base font-semibold hover:bg-action-orange-600 transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-whatsapp-line text-xl"></i>
                    Falar com Especialista
                  </a>
                  <a
                    href="/contato"
                    className="inline-flex items-center gap-2 px-8 py-4 bg-strategic-green-500 text-white font-semibold rounded-lg transition-all duration-300 hover:bg-strategic-green-600 hover:scale-105 hover:shadow-xl whitespace-nowrap"
                  >
                    Solicitar Análise Gratuita
                    <i className="ri-arrow-right-line text-xl"></i>
                  </a>
                </div>
              </div>
            </div>

          </div>
        </section>

        <Footer />
        {/* <ChatAssistant /> */}
      </div>
    </>
  );
}